import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import WorkerSelect from '../components/WorkerSelect';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR') : '');

export const STATUS_LABELS = {
  planned: 'Planlandı', planted: 'Ekildi / Dikildi', germinated: 'Çıkış Yaptı',
  growing: 'Büyüyor', ready: 'Hasada Hazır', harvested: 'Hasat Edildi', failed: 'Başarısız',
};
export const STATUS_COLORS = {
  planned: '#9e9e9e', planted: '#1976d2', germinated: '#00897b',
  growing: '#388e3c', ready: '#f57c00', harvested: '#795548', failed: '#d32f2f',
};
const WORK_TYPES = ['Arazi Hazırlığı', 'Ekim', 'Dikim', 'Fide Dikimi', 'Budama', 'Sulama', 'Diğer'];

export default function PlantingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('genel');
  const [labourForm, setLabourForm] = useState({ workerName: '', workType: 'Ekim', numWorkers: 1, dailyRate: '', days: 1, paymentStatus: 'pending' });
  const [expenseForm, setExpenseForm] = useState({ itemName: '', quantity: 1, unit: 'adet', unitPrice: '', paymentStatus: 'pending' });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/plantings/${id}/detail`).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addLabour = async () => {
    if (!labourForm.workerName) return;
    const total = (parseFloat(labourForm.dailyRate) || 0) * (parseFloat(labourForm.days) || 1) * (parseInt(labourForm.numWorkers) || 1);
    try {
      await api.post(`/plantings/${id}/labour`, { ...labourForm, totalAmount: total, workDate: new Date().toISOString().split('T')[0] });
      setLabourForm({ workerName: '', workType: 'Ekim', numWorkers: 1, dailyRate: '', days: 1, paymentStatus: 'pending' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eklenemedi'); }
  };
  const delLabour = async (lid) => { try { await api.delete(`/plantings/${id}/labour/${lid}`); load(); } catch { toast.error('Silinemedi'); } };

  const addExpense = async () => {
    if (!expenseForm.itemName) return;
    const total = (parseFloat(expenseForm.quantity) || 1) * (parseFloat(expenseForm.unitPrice) || 0);
    try {
      await api.post(`/plantings/${id}/expenses`, { ...expenseForm, totalAmount: total });
      setExpenseForm({ itemName: '', quantity: 1, unit: 'adet', unitPrice: '', paymentStatus: 'pending' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eklenemedi'); }
  };
  const delExpense = async (eid) => { try { await api.delete(`/plantings/${id}/expenses/${eid}`); load(); } catch { toast.error('Silinemedi'); } };

  if (loading) return <div className="empty-state"><p>Yükleniyor...</p></div>;
  if (!data) return <div className="empty-state"><p>Ekim kaydı bulunamadı</p><button className="btn" onClick={() => navigate('/plantings')}>← Geri</button></div>;

  const totals = data.totals || {};

  return (
    <div>
      <button onClick={() => navigate('/plantings')} style={{ background: 'none', border: 'none', color: 'var(--green-mid)', fontWeight: 700, cursor: 'pointer', padding: '0 0 8px' }}>← Ekimler</button>

      {/* Header */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1c3a1c' }}>{data.product_name} {data.variety && <span style={{ fontWeight: 400, color: '#888', fontSize: '0.9rem' }}>({data.variety})</span>}</div>
            <div style={{ fontSize: '0.8rem', color: '#7a9472' }}>
              {(data.field && data.field.name) || '—'}{data.field && data.field.size_decare ? ` (${data.field.size_decare} da)` : ''}
              {data.season && data.season.name && ` · ${data.season.name}`}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 2 }}>
              {fmtDate(data.planting_date)}{data.expected_harvest_date && ` → ${fmtDate(data.expected_harvest_date)}`}
            </div>
          </div>
          <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700, color: '#fff', background: STATUS_COLORS[data.status] || '#999' }}>
            {STATUS_LABELS[data.status] || data.status}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 10, fontSize: '0.78rem', flexWrap: 'wrap' }}>
          <span>🌰 {fmt(totals.seedCost)}</span>
          <span>👷 {fmt(totals.labourCost)}</span>
          <span>🛒 {fmt(totals.expenseCost)}</span>
          <span style={{ marginLeft: 'auto', fontWeight: 800 }}>Toplam: {fmt(totals.totalCost)}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="settings-tabs" style={{ marginBottom: 12 }}>
        {[['genel', '📋 Genel'], ['labour', '👷 İşçilik'], ['expenses', '🛒 Giderler'], ['history', '📅 Geçmiş']].map(([v, l]) => (
          <button key={v} className={`settings-tab${tab === v ? ' active' : ''}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {tab === 'genel' && (
        <div className="card">
          {[
            ['Ürün', data.product_name], ['Çeşit', data.variety], ['Tarla', data.field && data.field.name],
            ['Sezon', data.season && data.season.name], ['Yöntem', data.planting_method],
            ['Ekim Alanı', data.planting_area_decare ? `${data.planting_area_decare} da` : null],
            ['Ekim Tarihi', fmtDate(data.planting_date)], ['Tahmini Hasat', fmtDate(data.expected_harvest_date)],
            ['Ekim Normu', data.seeding_rate_kg_per_decare ? `${data.seeding_rate_kg_per_decare} kg/da` : null],
            ['Tohum', data.seed_product_name ? `${data.seed_quantity_used || ''} ${data.seed_display_unit || ''} ${data.seed_product_name}` : null],
            ['Notlar', data.notes],
          ].filter(([, v]) => v).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f2f2f2', fontSize: '0.85rem' }}>
              <span style={{ color: '#888' }}>{k}</span><span style={{ fontWeight: 600, textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'labour' && (
        <div className="card">
          {(data.labour || []).map(l => (
            <div key={l.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{l.worker_name}</div>
                <div className="txn-meta">{l.work_type} · {l.num_workers} kişi × {l.days} gün · {l.payment_status === 'paid' ? 'Ödendi' : 'Bekliyor'}</div>
              </div>
              <span style={{ fontWeight: 700, marginRight: 8 }}>{fmt(l.total_amount)}</span>
              <button onClick={() => delLabour(l.id)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
          {(!data.labour || data.labour.length === 0) && <div className="empty-state" style={{ padding: 12 }}><p style={{ fontSize: '0.82rem' }}>İşçilik kaydı yok</p></div>}

          <div style={{ borderTop: '1px solid #eee', marginTop: 8, paddingTop: 10 }}>
            <WorkerSelect label="İşçi Ekle" value={labourForm.workerName}
              onChange={v => setLabourForm(f => ({ ...f, workerName: v }))}
              onWorkerSelect={w => setLabourForm(f => ({ ...f, workerName: `${w.first_name} ${w.last_name}`, dailyRate: w.default_daily_rate || f.dailyRate }))}
              placeholder="Ahmet..." />
            <div className="form-row">
              <div className="form-group"><label>İş Türü</label><select value={labourForm.workType} onChange={e => setLabourForm(f => ({ ...f, workType: e.target.value }))}>{WORK_TYPES.map(w => <option key={w} value={w}>{w}</option>)}</select></div>
              <div className="form-group"><label>Kişi</label><input type="number" min="1" value={labourForm.numWorkers} onChange={e => setLabourForm(f => ({ ...f, numWorkers: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Gün</label><input type="number" step="0.5" value={labourForm.days} onChange={e => setLabourForm(f => ({ ...f, days: e.target.value }))} /></div>
              <div className="form-group"><label>Günlük (₺)</label><input type="number" value={labourForm.dailyRate} onChange={e => setLabourForm(f => ({ ...f, dailyRate: e.target.value }))} /></div>
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={addLabour} disabled={!labourForm.workerName}>➕ İşçilik Ekle</button>
          </div>
          <div style={{ fontWeight: 800, textAlign: 'right', marginTop: 10 }}>Toplam: {fmt(totals.labourCost)}</div>
        </div>
      )}

      {tab === 'expenses' && (
        <div className="card">
          {(data.expenses || []).map(e => (
            <div key={e.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{e.item_name}{e.reduces_stock ? ' 📦' : ''}</div>
                <div className="txn-meta">{e.quantity} {e.unit}{e.unit_price ? ` × ${fmt(e.unit_price)}` : ''} · {e.payment_status === 'paid' ? 'Ödendi' : 'Bekliyor'}</div>
              </div>
              <span style={{ fontWeight: 700, marginRight: 8 }}>{fmt(e.total_amount)}</span>
              <button onClick={() => delExpense(e.id)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
          {(!data.expenses || data.expenses.length === 0) && <div className="empty-state" style={{ padding: 12 }}><p style={{ fontSize: '0.82rem' }}>Gider kaydı yok</p></div>}

          <div style={{ borderTop: '1px solid #eee', marginTop: 8, paddingTop: 10 }}>
            <div className="form-group"><label>Gider Ekle</label><input value={expenseForm.itemName} onChange={e => setExpenseForm(f => ({ ...f, itemName: e.target.value }))} placeholder="İlaç, Naylon..." /></div>
            <div className="form-row">
              <div className="form-group"><label>Miktar</label><input type="number" value={expenseForm.quantity} onChange={e => setExpenseForm(f => ({ ...f, quantity: e.target.value }))} /></div>
              <div className="form-group"><label>Birim Fiyat (₺)</label><input type="number" value={expenseForm.unitPrice} onChange={e => setExpenseForm(f => ({ ...f, unitPrice: e.target.value }))} /></div>
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={addExpense} disabled={!expenseForm.itemName}>➕ Gider Ekle</button>
          </div>
          <div style={{ fontWeight: 800, textAlign: 'right', marginTop: 10 }}>Toplam: {fmt(totals.expenseCost)}</div>
        </div>
      )}

      {tab === 'history' && (
        <div className="card"><div className="empty-state" style={{ padding: 16 }}><div className="icon">📅</div><p>Yakında: Durum geçmişi</p></div></div>
      )}
    </div>
  );
}
